import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { eq } from 'ember-truth-helpers';

import type AlertingService from '../../services/alerting.ts';
import styles from './alert.module.css';
import PrismButton from './button.gts';
import PrismIllustration from './illustration.gts';

const DIALOG_RETURN_VALUE = 'closedByButton';

type AlertButton = {
  className?: string;
  isConfirmButton: boolean;
  text?: string;
  type: 'critical' | 'primary' | 'secondary';
};

interface PrismAlertSignature {
  Args: {};
}

export default class PrismAlert extends Component<PrismAlertSignature> {
  @service declare alerting: AlertingService;

  @tracked dialogElement: HTMLDialogElement | null = null;

  showDialog = modifier((element: HTMLDialogElement): void => {
    this.dialogElement = element;

    element.showModal();
  });

  get prismButtons(): AlertButton[] {
    const buttons = this.alerting.activeAlert?.buttons;
    const prismButtons: AlertButton[] = [];

    if (!buttons) {
      return prismButtons;
    }

    if (buttons['cancel']) {
      prismButtons.push({
        ...buttons['cancel'],
        isConfirmButton: false,
        type: 'secondary',
      });
    }

    if (buttons['confirm']) {
      const type = this.alerting.activeAlert?.dangerMode
        ? 'critical'
        : 'primary';

      prismButtons.push({
        ...buttons['confirm'],
        isConfirmButton: true,
        type,
      });
    }

    return prismButtons;
  }

  /**
   * Explicit action using one of the buttons, requiring a manual `.close` call
   */
  @action handleButtonClick({
    isConfirmButton,
    unsafe_canCloseAlert = true,
  }: {
    isConfirmButton: boolean;
    unsafe_canCloseAlert?: boolean;
  }): void {
    if (unsafe_canCloseAlert) {
      this.dialogElement!.close(DIALOG_RETURN_VALUE);
    }

    this.alerting.onClose({
      hasConfirmed: isConfirmButton,
      unsafe_canCloseAlert,
    });
  }

  /**
   * Handler for the dialog close event's. Listens to native functionality (like closing using Esc).
   * In this case `.close` is called implicitly, not requiring us to call it again.
   */
  @action handleDialogClose(unsafe_canCloseAlert = true, event: Event): void {
    if (!unsafe_canCloseAlert) {
      return;
    }

    const dialog = event.currentTarget as HTMLDialogElement;
    // If the dialog has been closed by a button click, we don't want to trigger the handler another time
    if (dialog.returnValue === DIALOG_RETURN_VALUE) {
      return;
    }

    this.alerting.onClose({
      hasConfirmed: false,
    });
  }

  <template>
    {{#let this.alerting.activeAlert as |activeAlert|}}
      {{#if activeAlert}}
        <dialog
          class={{styles.container}}
          closedby={{if
            (eq activeAlert.unsafe_outsideClickCanCloseAlert false)
            "none"
            "any"
          }}
          data-test-prism-alert
          {{on
            "close"
            (fn
              this.handleDialogClose
              activeAlert.unsafe_outsideClickCanCloseAlert
            )
          }}
          {{this.showDialog}}
        >
          {{#if activeAlert.icon}}
            <div class={{styles.illustration}}>
              <PrismIllustration @name={{activeAlert.icon}} @size="medium" />
            </div>
          {{/if}}

          <h1 class={{styles.title}} data-test-title>
            {{activeAlert.title}}
          </h1>

          <p class={{styles.text}} data-test-text>
            {{activeAlert.text}}
          </p>

          {{#if activeAlert.htmlContent}}
            {{activeAlert.htmlContent}}
          {{/if}}

          <div class={{styles.footer}}>
            {{#each this.prismButtons as |prismButton|}}
              <PrismButton
                @buttonType={{prismButton.type}}
                @label={{prismButton.text}}
                class={{prismButton.className}}
                data-test-button={{prismButton.text}}
                {{on
                  "click"
                  (fn
                    this.handleButtonClick
                    (hash
                      isConfirmButton=prismButton.isConfirmButton
                      unsafe_canCloseAlert=activeAlert.unsafe_buttonClickCanCloseAlert
                    )
                  )
                }}
              />
            {{/each}}
          </div>
        </dialog>
      {{/if}}
    {{/let}}
  </template>
}
